// Погодна карта — безкоштовний iframe-віджет Windy (embed2.html), без
// API-ключа. Свідомо обрано замість Windy Map Forecast API (JS + libBoot):
// той варіант вимагає глобальний Leaflet 1.4.x, конфліктує з нашим
// react-leaflet 1.9.x, відхиляє шар 'clouds' на цьому тарифі й малює свій
// UI під повноекранний windy.com (контроли з position:fixed і opacity:0).
// Iframe натомість самодостатній: власні контроли, легенда й перемикач
// шарів уже всередині, нічого не треба форсувати ззовні.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';

interface Props {
  lat: number;
  lon: number;
  height?: number | string;
  overlay?: string;
  zoom?: number;
}

// Windy без параметра lang визначає мову сам — за мовою браузера та
// геолокацією, — і для України зазвичай віддає російську. Тому мову задаємо
// явно й беремо її з перемикача кабінету, а не зашиваємо: UA/PL/EN мають
// давати карту тією ж мовою, що й решта інтерфейсу.
const WINDY_LANG: Record<string, string> = { uk: 'uk', pl: 'pl', en: 'en' };

export default function WeatherMap({ lat, lon, height = 320, overlay = 'clouds', zoom = 13 }: Props) {
  const { i18n, t } = useTranslation();
  const lang = WINDY_LANG[i18n.language?.slice(0, 2) ?? 'uk'] ?? 'uk';

  // Панель «прогноз для цього місця» розгортається всередині iframe, і
  // закрити її ззовні неможливо: це чужий домен, доступу до його DOM немає.
  // Тому даємо власну кнопку скидання — зміна ключа перемонтовує iframe,
  // і він вантажиться заново з початковими параметрами.
  const [resetKey, setResetKey] = useState(0);

  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    detailLat: String(lat),
    detailLon: String(lon),
    zoom: String(zoom),
    level: 'surface',
    overlay,
    product: 'ecmwf',
    menu: '',
    message: '',
    marker: 'true',
    calendar: 'now',
    pressure: '',
    type: 'map',
    location: 'coordinates',
    detail: '',
    metricWind: 'm/s',
    metricTemp: '°C',
    radarRange: '-1',
    lang,
  });

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <iframe
        // Ключ містить і мову, і лічильник скидань: зміна будь-чого з них
        // перемонтовує iframe. Без цього він лишається в попередньому стані.
        key={`${lang}-${resetKey}`}
        title="Windy"
        src={`https://embed.windy.com/embed2.html?${params.toString()}`}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        loading="lazy"
      />
      <button
        type="button"
        onClick={() => setResetKey((k) => k + 1)}
        title={t('fc_map_reset')}
        aria-label={t('fc_map_reset')}
        className="absolute flex items-center justify-center cursor-pointer transition-colors"
        style={{
          top: 8, left: 8, width: 28, height: 28, borderRadius: 8, zIndex: 2,
          background: 'var(--surface-solid)', border: '1px solid var(--line)',
          color: 'var(--text-muted)', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}
      >
        <RotateCcw size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
